// ── Auth ──────────────────────────────────────────────────────────────────────

export interface User {
  id: string
  email: string
  display_name: string | null
  avatar_url: string | null
  plan: 'free' | 'pro'
  plan_expires_at: string | null
  trial_ends_at: string | null
  created_at: string
  // v2 fields
  source_language: string | null
  target_language: string | null
  current_level: string | null
  onboarding_completed: boolean
  interests: string[]
  daily_card_goal: number
  words_known_count: number
  show_translations: boolean
  notifications_enabled: boolean
  is_admin: boolean
  // v2.1 multilingual UX fields
  explanation_language: string | null
  interface_language: string | null
  learning_languages: string[] | null
  // P6.5 funnel analytics flags
  first_card_studied_at: string | null
  day_1_return_fired: boolean
  day_7_return_fired: boolean
  // True when the user can still sign in by email + password.  False for
  // OAuth-only accounts — Settings uses this to forbid removing the only
  // remaining sign-in method.
  has_password: boolean
}

// ── Languages (v2) ─────────────────────────────────────────────────────────────

export interface Language {
  code: string
  name: string
  flag: string
}

export interface LanguagePair {
  source_language: string
  target_language: string
  course_count?: number
}

export interface OnboardingPayload {
  source_language: string
  target_language: string
  current_level: string
  interests: string[]
}

export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
}

// ── Folders ───────────────────────────────────────────────────────────────────

export interface Folder {
  id: string
  title: string
  deck_count: number
  created_at: string
  updated_at: string
}

export interface FolderListResponse {
  items: Folder[]
  total: number
  page: number
  page_size: number
}

// ── Decks ─────────────────────────────────────────────────────────────────────

export interface Deck {
  id: string
  user_id: string | null
  title: string
  description: string | null
  folder_id: string | null
  folder_title: string | null
  is_public: boolean
  is_system: boolean
  is_inbox: boolean
  share_token: string | null
  default_prompt_side: string
  language: string
  cefr_level: string | null
  card_count: number
  due_count: number | null
  /** Cards at mastery_level >= 5 (same ruler as Progress screen's words-known counter). */
  learned_count: number
  /** Texts linked to the deck (ADR-115). The Mine list fills it; null elsewhere. */
  text_count?: number | null
  /** Whether this deck participates in the smart queue (default true) */
  is_active_for_learning: boolean
  created_at: string
  updated_at: string
}

export interface DeckLearningToggleResponse {
  deck_id: string
  is_active_for_learning: boolean
}

export interface DeckDisplayPreference {
  deck_id: string
  front_side: 'term-front' | 'definition-front'
  collapsed_parts: string[] | null
}

export interface KindCoverageItem {
  kind: string
  card_count: number
  total_cards: number
  fraction: number
}

export interface DeckKindsResponse {
  deck_id: string
  kinds: KindCoverageItem[]
}

export interface QueueSourceItem {
  deck_id: string
  title: string
  card_count: number
  is_system: boolean
  is_active_for_learning: boolean
  due_count: number
  new_count: number
}

export interface QueueCompositionResponse {
  sources: QueueSourceItem[]
}

export interface DeckListResponse {
  items: Deck[]
  total: number
  page: number
  page_size: number
}

export interface DeckCreate {
  title: string
  description?: string
  folder_id?: string
  is_public?: boolean
  default_prompt_side?: string
  language?: string
  is_active_for_learning?: boolean
}

export interface DeckUpdate {
  title?: string
  description?: string | null  // null clears the description (see handleSaveDescription)
  folder_id?: string | null
  is_public?: boolean
  default_prompt_side?: string
  language?: string
}

// ── Cards ─────────────────────────────────────────────────────────────────────

export interface CardSide {
  id: string
  label: string
  content: string
  position: number
  /** Machine identity (ADR-040). Null for not-yet-backfilled rows. */
  kind?: string | null
  /** Grammatical-form hint (e.g. "verb, past tense"). Null when not available. See ADR-021. */
  form?: string | null
}

export interface Card {
  id: string
  deck_id: string
  position: number
  sides: CardSide[]
  mastery_level: number | null
  share_token: string | null
  translation?: string | null
  created_at: string
  updated_at: string
  /** Corpus linkage (ADR-063 §4). Null for unlinked cards. */
  word_entry_id?: string | null
  /** This user's MasteryState schedule. Both null/false when no MasteryState
   * row exists yet — a card never studied, not a guessed level. Naive-UTC,
   * same as every other backend timestamp (append 'Z' before parsing). */
  due_at?: string | null
  is_mastered?: boolean
}

export interface CardListResponse {
  items: Card[]
  total: number
  page: number
  page_size: number
}

/** A search hit: an ordinary card plus `index`, its 0-based ordinal in the
 * deck's canonical order. The ordinal is what makes the hit navigable — it
 * becomes a page number (`index / page_size | 0` + 1) so one request fetches
 * the card's page rather than every page up to it (brief §21.3 item 1). */
export interface CardSearchItem extends Card {
  index: number
}

export interface CardSearchResponse {
  items: CardSearchItem[]
  /** Every match in the DECK, not on this page — the `FOUND` counter's
   * denominator (brief §13). A count over the pages the client happened to have
   * loaded is the defect this endpoint removes (ADR-067 Decision 6). */
  total: number
  page: number
  page_size: number
}

export interface CardLocateResponse {
  card_id: string
  index: number
  position: number
  total: number
}

export interface CardSideInput {
  /** The stored side this entry IS (ADR-074). Supplied so a full-replace write
   * keeps the row rather than minting a new id, which is what lets the editing
   * draft tell a late copy of a field from a different field. Omitted for a
   * field the server has not answered for yet; a backend older than ADR-074
   * ignores it and behaves exactly as before. */
  id?: string
  label: string
  content: string
  /** Explicit kind (ADR-040). Backend prefers this over label-derivation when supplied. */
  kind?: string
}

// ── Lookup ────────────────────────────────────────────────────────────────────

export interface ExistingCardLocation {
  card_id: string
  deck_id: string
  deck_title: string
  is_inbox: boolean
  is_system: boolean
  last_review: string | null
  card_created_at: string
}

export interface LookupResult {
  query: string
  lemma: string
  transcription: string | null
  translation: string | null
  definition: string | null
  example: string | null
  audio_available: boolean
  word_entry_id: string | null
  found: boolean
  // WordEntry structured metadata
  pos: string | null
  register: string | null
  variant: string | null
  function: string | null
  collocations: string | null
  form_of: string | null
  existing_locations: ExistingCardLocation[]
}

export interface LookupAddResult {
  card_id: string
  deck_id: string
  already_present: boolean
}

export interface CardCreate {
  sides: CardSideInput[]
}

export interface CardUpdate {
  sides?: CardSideInput[]
  position?: number
}

// ── Courses ───────────────────────────────────────────────────────────────────

export interface CourseDeckSummary {
  id: string
  title: string
  card_count: number
  position: number
}

export interface CourseFolderContent {
  id: string
  title: string
  decks: CourseDeckSummary[]
  position: number
}

// One entry in a course reorder request — decks and folders share one sequence.
export interface CourseReorderItem {
  type: 'deck' | 'folder'
  id: string
  position: number
}

export interface Course {
  id: string
  title: string
  description: string | null
  is_public: boolean
  share_token: string | null
  deck_count: number
  folder_count: number
  created_at: string
  updated_at: string
}

export interface CourseContent {
  id: string
  title: string
  description: string | null
  is_public: boolean
  share_token: string | null
  folders: CourseFolderContent[]
  standalone_decks: CourseDeckSummary[]
  created_at: string
  updated_at: string
}

export interface CourseCreate {
  title: string
  description?: string
  is_public?: boolean
}

export interface CourseUpdate {
  title?: string
  description?: string
  is_public?: boolean
}

export interface CourseListResponse {
  items: Course[]
  total: number
  page: number
  page_size: number
}

// ── Public ────────────────────────────────────────────────────────────────────

export interface PublicDeck extends Deck {
  author_name: string | null
  is_copied: boolean
  is_enrolled: boolean
}

export interface PublicDeckListResponse {
  items: PublicDeck[]
  total: number
  page: number
  page_size: number
}

export interface PublicCourse extends Course {
  author_name: string | null
}

export interface PublicCourseListResponse {
  items: PublicCourse[]
  total: number
  page: number
  page_size: number
}

// ── Shared ────────────────────────────────────────────────────────────────────

export interface SharedDeck {
  id: string
  title: string
  description: string | null
  language: string
  share_token: string
  default_prompt_side: string
  card_count: number
  cards: Card[]
  created_at: string
  updated_at: string
}

export interface SharedCourse {
  id: string
  title: string
  description: string | null
  share_token: string
  folders: CourseFolderContent[]
  standalone_decks: CourseDeckSummary[]
  created_at: string
  updated_at: string
}

// ── Study ─────────────────────────────────────────────────────────────────────

export type StudyGrade = 'know' | 'dont_know'

export interface ClassicCardSide {
  id: string
  label: string
  content: string
  /** Machine identity (ADR-040). Null for not-yet-backfilled rows. Used to resolve the Term side (kind-first, label-fallback). */
  kind?: string | null
}

export interface ClassicSessionProgress {
  total: number
  reviewed: number
  known: number
  unknown: number
  remaining: number
}

export interface ClassicSession {
  id: string
  mode: 'classic'
  status: 'active' | 'completed'
  source_id: string | null
  shuffle: boolean
  progress: ClassicSessionProgress
  started_at: string
  completed_at: string | null
}

export interface ClassicNextCard {
  card_id: string
  front_side: ClassicCardSide
  back_sides: ClassicCardSide[]
  remaining: number
  position: number
  // Ordered look-ahead window (ADR-038): cards after this one in the queue, up
  // to CLASSIC_LOOK_AHEAD entries (empty at the tail). Replaces following_card.
  upcoming: ClassicNextCard[]
}

export interface GradeCardResponse {
  progress: ClassicSessionProgress
  next_card: ClassicNextCard | null
}

// ── Learning ──────────────────────────────────────────────────────────────────

export type BlockType = 'new_words' | 'review' | 'mixed_practice'

export interface LessonBlock {
  block_index: number
  block_type: BlockType
  card_count: number
  card_ids: string[]
}

export interface LessonPlan {
  blocks: LessonBlock[]
  total_interactions: number
}

export interface PrepStats {
  /** Cards selected for this prep session. */
  challenging_count: number
  /** Total non-mastered cards in the deck(s) at session creation time. */
  total_deck_count: number
}

export interface LearningSessionCreateResponse {
  session_id: string
  total_cards: number
  due_cards: number
  new_cards: number
  is_resumed: boolean
  /** Cards still unreviewed in a resumed session; drives the progress-bar offset. */
  remaining_cards: number
  /** Authoritative conveyor-resolution seed (ADR-046). Seeds the live counter on
   *  resume — distinct from (total_cards − remaining_cards), which undercounts
   *  in-flight non-graduated resolutions. 0 for a fresh session. */
  resolution_count: number
  /** Lesson plan with block structure. Null for custom/review-mistakes sessions. */
  lesson_plan?: LessonPlan | null
  /** Present when a prep session was created with enough challenging cards. */
  prep_stats?: PrepStats | null
  /** ADR-112 Decision 6: a practice session (Train, Quick prep, topic training), in
   *  which no answer changes a word's level, showings or review date. */
  is_practice?: boolean
  /** True when a previous session of this user's was closed by today's day
   *  boundary (ADR-082) rather than by the person finishing it. Drives the
   *  Learn boundary overlay's "returned" copy. Default false covers older
   *  server responses that predate the field. */
  day_boundary_closed_previous?: boolean
}

export interface LearningSessionStatus {
  id: string
  status: 'active' | 'paused' | 'completed'
  source_type: string
  total: number
  reviewed: number
  correct: number
  wrong: number
  due_remaining: number
  new_words: number
  /** Authoritative conveyor-resolution counter (ADR-046) — the live-counter seed on
   *  resume/reload. Distinct from `reviewed` (graduation count). */
  resolution_count: number
}

export interface SideInfo {
  label: string
  content: string
  /** Grammatical-form hint (e.g. "verb, past tense"). Null when not available. See ADR-021. */
  form?: string | null
  /** Machine identity (ADR-040). Null for not-yet-backfilled rows. Used to resolve the Term side (kind-first, label-fallback). */
  kind?: string | null
  /** ADR-100 Decision 6: this side's translation, language code → text. Only on the
   * Definition Learn reads, only for the reader's source_language, fresh rows only —
   * so at most one entry. Carried whatever `show_translation` says; the flag decides
   * whether it is shown. Absent from payloads older than ADR-100. */
  translations?: Record<string, string>
}

/** Contextual grammar micro-note shown once per lesson when a word triggers a pattern. */
export interface GrammarNote {
  title: string
  note_text: string
  check_question?: string | null
  check_answer?: string | null
  cefr_level?: string | null
}

export interface LearningNextCard {
  session_complete: false
  card_id: string
  /** ADR-112 Decision 6: served by a practice session — no answer changes the word's level. */
  is_practice?: boolean
  prompt: SideInfo
  other_sides: SideInfo[]
  mastery_level: number
  translation?: string | null
  /**
   * Suggested exercise format from the backend, derived from mastery level + card sides.
   * "multiple_choice"      — mastery 1 (variety slot) with ≥3 distractors: 4 tappable options
   * "fill_blank_supported" — legacy mastery 1 + example: full context, translation forced on
   * "fill_blank"           — mastery 2-3 + example: FiB, no forced translation
   * "definition_only"      — mastery 4-5 or no example: definition prompt, hint allowed
   * "definition_strict"    — mastery 6+ or no example: definition prompt, no hint
   * "sentence_build"       — mastery 4-5 + example: arrange scrambled word chips
   * Frontend may override based on user preferences.
   */
  exercise_format: 'multiple_choice' | 'fill_blank_supported' | 'fill_blank' | 'definition_only' | 'definition_strict' | 'listening' | 'sentence_build'
  /** Populated when exercise_format === "multiple_choice": exactly 3 distractor terms. */
  distractors?: string[]
  /** Populated when exercise_format === "sentence_build": scrambled word chips (correct + distractors). */
  sentence_words?: string[]
  /** Populated when exercise_format === "sentence_build": the correct sentence in canonical order. */
  sentence_correct?: string
  /** Backend-computed hint visibility flags — frontend must not re-derive these from mastery_level. */
  show_translation?: boolean
  show_definition?: boolean
  show_part_of_speech?: boolean
  show_form?: boolean
  /** Lesson block metadata — null for sessions without lesson planning. */
  block_type?: BlockType | null
  block_index?: number | null
  /** 1-based position of this card within its block (e.g. 3 means "card 3 of N"). */
  block_position?: number | null
  /** Initial card count for this block (denominator for block_position). */
  block_total?: number | null
  /**
   * Contextual grammar micro-note — present when this card's Term matches a known
   * grammar pattern and no note has been delivered yet this session. Null otherwise.
   * The note should be shown AFTER the user answers this card (between exercises).
   */
  grammar_note?: GrammarNote | null
}

/** Returned by GET /next-card when the session queue is exhausted.
 *  total = distinct cards answered (== reviewed); correct + wrong == total always. */
export interface SessionCompleteSignal {
  session_complete: true
  total: number       // distinct reviewed cards (not queue size)
  correct: number     // total − wrong
  wrong: number       // distinct reviewed cards with ≥1 error
  new_words: number   // per-session new cards
  new_words_today?: number  // cross-session daily new-card count
}

/** Discriminated union — check `session_complete` before accessing card fields. */
export type NextCardData = LearningNextCard | SessionCompleteSignal

export interface AnswerCorrectResult {
  is_correct: true
  /** Discriminant: absent/false here so TS can narrow soft-reject out of the union. */
  is_soft_reject?: false
  correct_answer: string
  user_answer: string
  mastery_level: number
  mastery_level_before: number
  mastery_change: number
  is_mastered: boolean
  next_due_description: string
  /** True when the card left the active queue (server is_reviewed=True). */
  graduated: boolean
}

export interface AnswerWrongResult {
  is_correct: false
  /** Discriminant: absent/false here so TS can narrow soft-reject out of the union. */
  is_soft_reject?: false
  correct_answer: string
  user_answer: string
  require_retype: boolean
  mastery_level: number
  mastery_level_before: number
  mastery_change: number
  /** Always absent on a wrong answer (only correct answers can be mastered);
   *  optional-undefined so union-wide reads type as `boolean | undefined`. */
  is_mastered?: undefined
}

/** Synonym-match outcome — neither correct nor wrong (ADR-048 §§1–2). Zero state writes. */
export interface AnswerSoftRejectResult {
  is_correct: false
  is_soft_reject: true
  /** First character of the expected term — shown as a hint. */
  first_letter: string
  mastery_level: number
  mastery_level_before: number
  // These fields are always ABSENT on a soft reject (the server omits them —
  // see backend AnswerSoftRejectResponse). Declared here as optional-undefined
  // so union-wide reads type as `T | undefined` and the existing `?? fallback`
  // call sites compile without a runtime change. TRADE-OFF (accepted): this
  // legalises reading these fields anywhere on the union, so TS no longer flags
  // future call sites that forget to handle soft reject. The proper end-state is
  // a discriminated union — see docs/diagnostics/learn-soft-reject-union-latent-bug.md.
  correct_answer?: undefined
  require_retype?: undefined
  mastery_change?: undefined
  is_mastered?: undefined
}

export type LearningAnswerResult = AnswerCorrectResult | AnswerWrongResult | AnswerSoftRejectResult

/**
 * Client-only record of a wrong answer, accumulated in memory during a Learn
 * session for the read-only "Review mistakes" list (no backend persistence,
 * no ReviewLog read). One entry per card — see recordMistake in
 * useLearningSession.ts for the dedup rule.
 */
export interface MistakeRecord {
  cardId: string
  term: string
  formLabel: string | null
  userAnswer: string
  correctAnswer: string
  definition: string | null
  translation: string | null
}

export interface RetypeResult {
  accepted: boolean
  message?: string
}

export interface DeckStats {
  levels: Record<number, number>
  mastered: number
  total: number
}

// ── Mastery ───────────────────────────────────────────────────────────────────
// The scale is a number 1-8 (how well a term is learned), not a set of words —
// no per-level label map. See MASTERY_MAX below for the one place "8" is named.

export const MASTERY_COLORS: Record<number, string> = {
  1: 'var(--mastery-1)',
  2: 'var(--mastery-2)',
  3: 'var(--mastery-3)',
  4: 'var(--mastery-4)',
  5: 'var(--mastery-5)',
  6: 'var(--mastery-6)',
  7: 'var(--mastery-7)',
  8: 'var(--mastery-8)',
}

// Top of the mastery ladder — derived from the color map so MasteryBadge's dot
// segment count and level clamping rescale automatically if levels ever change.
export const MASTERY_MAX: number = Math.max(...Object.keys(MASTERY_COLORS).map(Number))

// ── Catalog ───────────────────────────────────────────────────────────────────

export interface CatalogDeck {
  id: string
  title: string
  description: string | null
  language: string
  cefr_level: string | null
  topic: string | null
  card_count: number
  is_active_for_learning: boolean
  learned_count: number
}

export interface CatalogFolder {
  id: string
  title: string
  decks: CatalogDeck[]
}

export interface CatalogCourse {
  id: string
  title: string
  description: string | null
  language: string
  cefr_level: string | null
  topic_count: number
  deck_count: number
  card_count: number
  is_enrolled: boolean
}

export interface CatalogCourseDetail extends CatalogCourse {
  folders: CatalogFolder[]
  decks: CatalogDeck[]
}

export interface CatalogListResponse {
  items: CatalogCourse[]
  total: number
  page: number
  page_size: number
  available_languages: string[]
  available_levels: string[]
}

export interface CatalogDeckListResponse {
  items: CatalogDeck[]
  total: number
  page: number
  page_size: number
  available_levels: string[]
}

// ── Grammar ───────────────────────────────────────────────────────────────────

export interface GrammarTopicBrief {
  slug: string
  title: string
  cefr_level: string
}

export interface GrammarTopic {
  id: string
  title: string
  slug: string
  cefr_level: string
  topic_group: string
  content_html: string
  related_deck_id: string | null
  related_deck_title: string | null
}

export interface GrammarGroup {
  group: string
  count: number
  topics: GrammarTopicBrief[]
}

export interface GrammarListResponse {
  items: GrammarTopic[]
  total: number
  page: number
  page_size: number
  available_groups: string[]
  available_levels: string[]
}

// ── Progress (v2) ─────────────────────────────────────────────────────────────

export interface MasteryBreakdown {
  new: number      // mastery level 1
  fragile: number  // mastery levels 2-3
  active: number   // mastery levels 4-6
  mastered: number // mastery levels 7-8
}

export interface ProgressSummary {
  words_known_count: number
  /** User's proficiency level A1–C2 — set at onboarding, moved only by assessment */
  current_level: string
  /** Vocabulary-volume band A1–C2 inferred from words_known_count thresholds */
  vocab_band: string
  /** 0–100 progress within the current vocabulary band */
  level_progress: number
  due_review_count: number
  /** Unseen cards in the user's decks — the pool the queue draws new-card slots from */
  new_cards_available: number
  /** Consecutive days with at least one review */
  streak_days: number
  /** All-time best consecutive-day streak */
  best_streak: number
  /** Total distinct cards reviewed today (new + reviews) */
  today_cards_reviewed: number
  /** Daily-goal progress (session-credit rule): sum of today's Learning-mode
   *  sessions' contributions — a completed session contributes its nominal
   *  size (queue size) regardless of mistakes/retries; an interrupted
   *  (active/paused) session contributes its distinct-cards-answered-so-far
   *  count. Numerator for the daily goal ring: today_resolutions / daily_card_goal. */
  today_resolutions: number
  /** User's daily goal in cards — always a multiple of 40 (1–3 tabata sessions) */
  daily_card_goal: number
  /** Estimated % of everyday running text covered by known words */
  text_coverage_pct: number
  /** Words needed to reach the next CEFR band (null at C2) */
  words_to_next_level: number | null
  /** Projected days to next level at current 7-day pace (null if too slow or at C2) */
  projected_days_to_next_level: number | null
  /** Card counts by mastery tier — null on legacy cached responses */
  mastery_breakdown?: MasteryBreakdown | null
  /** Words known within the current CEFR band (relative to band lower bound) */
  level_words_known?: number
  /** Total word span of the current CEFR band (null at C2) */
  level_words_total?: number | null
}

// ── Vocabulary map ────────────────────────────────────────────────────────────

export interface TopicCluster {
  topic: string
  total_available: number
  known: number
  in_progress: number
  coverage_pct: number
  trainable_cards: number
}

export interface VocabularyMapResponse {
  clusters: TopicCluster[]
  suggested_topics: string[]
  target_language: string
  user_level: string
}

// ── Difficult words ───────────────────────────────────────────────────────────

export interface DifficultWordItem {
  card_id: string
  term: string
  error_count: number
}

export interface DifficultWordsResponse {
  words: DifficultWordItem[]
}

// ── Active Session ────────────────────────────────────────────────────────────

export interface ActiveSession {
  session_id: string
  source_type: string
  source_title: string | null
}

/** A deck the user recently focus-studied that still has actionable material.
 *  Powers the Home "jump back into a deck" cards. The endpoint only returns decks
 *  with due reviews or unstarted new cards, so this list is either non-empty or
 *  absent — there is no zero/empty state to render. */
export interface RecentDeck {
  deck_id: string
  title: string
  card_count: number
  /** Cards at mastery level ≥ 5 — numerator for the single-hue DeckProgressBar. */
  learned_count: number
  /** Reviews due right now. */
  due_count: number
}

// ── Smart Queue (v2) ──────────────────────────────────────────────────────────

export interface SmartQueueSessionResponse {
  session_id: string | null
  total_cards: number
  review_cards: number
  new_cards: number
  /** ADR-103 Decision 1: the start returned today's live stream session rather than
   *  building one. `total_cards` is then that session's unchanged size. */
  is_resumed: boolean
  /** Live-counter seed on a resume (ADR-046); 0 for a new session. */
  resolution_count: number
  /** ADR-082 Decision 6 signal, scoped to this call. */
  day_boundary_closed_previous: boolean
}

export interface TopicTrainSessionResponse {
  session_id: string
  total_cards: number
  topic: string
}

// ── Text Import (v2) ──────────────────────────────────────────────────────────

export interface WordCandidateResponse {
  word: string
  lemma: string
  pos: string
  cefr_level: string | null
  sentence: string
  translation: string | null
  notes: string | null
  below_level: boolean
  /** P5.1+: how this word ranks relative to the user's current level */
  relevance_group?: 'recommended' | 'advanced' | 'likely_known'
  /** "Already studying" words only: the learner's own decks that hold a card for it. */
  deck_ids?: string[]
}

export interface TextImportExtractRequest {
  /** Raw text to extract from — provide either text or url, not both */
  text?: string | null
  /** URL to fetch and extract from — provide either text or url, not both */
  url?: string | null
  title?: string | null
  language?: string | null
}

export interface TextImportExtractResponse {
  text_import_id: string
  /** ISO 639-1 target language used for this import — e.g. "en" */
  language: string
  /** @deprecated use total_found */
  words_total: number
  /** @deprecated use already_known */
  words_known: number
  candidates: WordCandidateResponse[]
  /**
   * The rest of the text's words, collapsed and unselected but still selectable, so
   * no word disappears: every distinct word is in `candidates` or in exactly one of
   * these. Items carry the same fields as a candidate. Absent from an older server.
   */
  groups?: {
    already_studying?: WordCandidateResponse[]
    already_known: WordCandidateResponse[]
    function_words: WordCandidateResponse[]
    names: WordCandidateResponse[]
  }
  /** Every distinct word of the text: candidates plus all three groups. */
  total_found: number
  /** The words in `groups.already_known`. */
  already_known: number
  below_level: number
  new_words: number
  /** Set when the import was initiated from a URL — null for plain-text imports */
  source_url: string | null
  source_title: string | null
  /** YouTube only: true when auto-generated captions were used, null for non-YouTube imports */
  subtitle_auto_generated: boolean | null
  /** Set when the import was initiated from a file upload — null for text/URL imports */
  source_filename: string | null
  /** P5.1+: the user's CEFR level at extraction time — null when not set */
  user_level?: string | null
}

/**
 * Everything the person entered on the text-import first step. Held in
 * frontend/src/utils/textImportFlowState.ts (in-memory, keyed to the
 * history entry) rather than in history state itself — see that module for
 * why.
 */
export interface TextImportDraft {
  text: string
  title: string
  importMode: 'new' | 'existing'
  pickerDeckId: string | null
  preTargetActive: boolean
  targetDeckTitle: string | null
  loadedFileName: string | null
  textFromOcr: boolean
}

export interface DismissLemmaItem {
  lemma: string
  language: string
}

export interface DismissWordsRequest {
  lemmas: DismissLemmaItem[]
}

export interface DismissWordsResponse {
  dismissed: number
}

export interface ConfirmedWord {
  word: string
  lemma: string
  pos: string
  sentence: string
  cefr_level: string | null
}

export interface TextImportCreateDeckRequest {
  text_import_id: string
  confirmed_words: ConfirmedWord[]
  title?: string | null
  target_deck_id?: string | null
  is_active_for_learning?: boolean
}

export interface TextImportCreateDeckResponse {
  text_import_id: string
  deck_id: string
  card_count: number
  words_skipped: number
}

/**
 * Why some cards were created without content (ADR-078). A stable code, never
 * prose and never a provider diagnostic — the sentence the reader sees is
 * chosen from this, in their own language. Unknown values are possible if the
 * server adds a code this build doesn't know, so treat the union as open.
 */
export type TextImportPendingReason =
  | 'AI_RATE_LIMITED'
  | 'AI_UNAVAILABLE'
  | 'AI_INCOMPLETE'

/**
 * Why confirmed words were deliberately not created. Same open-union contract
 * as TextImportPendingReason: a code, localized client-side.
 *
 * There is exactly one, and it is narrow: the word already had a card in the
 * deck being appended to. A word the person holds in some OTHER deck is not a
 * reason to refuse it (ADR-078 amendment) — a deck built from a text is a
 * self-contained set — so this can only arise when adding to an existing deck.
 */
export type TextImportSkippedReason = 'ALREADY_IN_DECK'

export interface TextImportStatusResponse {
  text_import_id: string
  deck_id: string | null
  status: 'extracting' | 'reviewing' | 'generating' | 'done' | 'failed'
  words_new: number
  /** The selection the person confirmed — the denominator for progress. 0 on imports created before this field existed. */
  words_confirmed: number
  /** Cards written: complete and content-pending alike. Never an estimate. */
  words_generated: number
  /** How many of those cards are still waiting for content. Subset of words_generated. */
  words_pending: number
  /** Words that produced no card at all. */
  words_failed: number
  /**
   * Confirmed words deliberately not created because the deck being appended to
   * already held them. Completes the account of the selection:
   *   words_confirmed = words_generated + words_failed + words_skipped
   * 0 on imports created before this field existed, and 0 on every new-deck run.
   */
  words_skipped: number
  /** Reason code for the skipped words; null when nothing was skipped. */
  skipped_reason: TextImportSkippedReason | string | null
  /** Reason code for the pending cards; null when nothing needed explaining. */
  error_message: TextImportPendingReason | string | null
}

// ── Admin panel ───────────────────────────────────────────────────────────────

export interface AdminDeck {
  id: string
  title: string
  description: string | null
  language: string
  cefr_level: string | null
  card_count: number
  is_public: boolean
  translation_languages: string[]
}

export interface AdminDeckUpdate {
  title?: string
  description?: string
  cefr_level?: string
  is_public?: boolean
}

export interface TranslationCoverage {
  source_language: string
  count: number
  missing: number
}

export interface TargetLanguageSummary {
  language: string
  total_entries: number
  translations: TranslationCoverage[]
}

export interface WordDictionarySummary {
  languages: TargetLanguageSummary[]
}

// ── Admin analytics (P6.5.3) ──────────────────────────────────────────────────

export interface DailyAIPoint {
  date: string
  actual: number
  cached: number
  total: number
}

export interface AIUsageResponse {
  items: DailyAIPoint[]
  total_actual: number
  total_cached: number
}

export interface DailyPaywallPoint {
  date: string
  endpoint: string
  count: number
}

export interface PaywallResponse {
  items: DailyPaywallPoint[]
  total: number
}

export interface FunnelResponse {
  registrations: number
  trials_started: number
  trials_converted: number
  churned: number
  period_start: string
  period_end: string
}

export interface MasteryBucket {
  bucket: string
  count: number
}

export interface TimeToMasteryResponse {
  buckets: MasteryBucket[]
  total: number
  median_days: number | null
}

// ── Admin scheduled-job visibility ────────────────────────────────────────────

/** Last recorded outcome of one recurring scheduled job. */
export interface JobRunRecord {
  job_id: string
  description: string
  /** "unknown" = no record found; never means healthy. */
  status: 'success' | 'skipped' | 'failed' | 'unknown'
  finished_at: string | null
  /** Free-form per job — the hourly reminder reports this run's numbers plus the day's totals. */
  summary: Record<string, string | number>
  error: string | null
}

export interface JobsResponse {
  items: JobRunRecord[]
}

// ── Admin tester-activity view ─────────────────────────────────────────────────

export interface UserActivityRow {
  user_id: string
  email: string
  registered_at: string
  onboarding_completed: boolean
  last_active_at: string | null
  days_since_active: number | null
  streak_days: number
  sessions_started: number
  sessions_completed: number
  classic_sessions_started: number
  learning_sessions_started: number
  answers_submitted: number
  answers_correct: number
  accuracy: number | null
  mastery_ladder: number[]
  mastered_count: number
}

export interface RetentionBucket {
  bucket: string
  cohort_size: number
  active: number
}

export interface WorstCard {
  card_id: string
  deck_id: string
  term: string | null
  attempts: number
  correct: number
  accuracy: number
}

export interface CohortSummary {
  total_users: number
  active_window_days: number
  active_this_week: number
  sessions_started: number
  sessions_completed: number
  sessions_abandoned: number
  abandonment_rate: number | null
  retention: RetentionBucket[]
}

export interface ActivityResponse {
  generated_at: string
  cohort: CohortSummary
  users: UserActivityRow[]
  worst_cards: WorstCard[]
}

// ── API errors ────────────────────────────────────────────────────────────────

// Mirror of the backend `ErrorCode` enum. MUST stay in sync with
// `backend/app/core/errors.py` — every code added there must be added here,
// and nothing here should exist that isn't in the backend enum.
export type ErrorCode =
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'PRO_REQUIRED'
  | 'UNAUTHORIZED'
  | 'RATE_LIMITED'
  | 'AI_UNAVAILABLE'
  | 'SERVICE_UNAVAILABLE'

export interface ApiError {
  detail: string
  code: ErrorCode
}

// ── Texts: the creation flow (ADR-115, handoff §3–§6, §16) ───────────────────

/** GET /api/v1/texts/limits. The frontend defines none of these numbers. */
export interface TextLimits {
  max_chars: number
  max_gaps: number
  max_custom_label_chars: number
}

/** One tappable word of the body: a span the gap matcher also accepts. */
export interface AnalysedWord {
  start: number
  end: number
  text: string
  /** Lemmas of the word's alphabetic tokens: one for most, several for a compound, none for "A1". */
  lemmas: string[]
  preselected: boolean
}

export interface TextAnalysisCandidate extends WordCandidateResponse {
  /** Suggested as Card on arrival (above the learner's level, not known). */
  preselected: boolean
}

export interface TextAnalysisCounts {
  distinct: number
  marked: number
  other_words: number
  already_studying: number
  already_known: number
  function_words: number
  names: number
}

export interface TextAnalysis {
  language: string
  user_level: string | null
  word_count: number
  words: AnalysedWord[]
  candidates: TextAnalysisCandidate[]
  groups: {
    already_studying: WordCandidateResponse[]
    already_known: WordCandidateResponse[]
    function_words: WordCandidateResponse[]
    names: WordCandidateResponse[]
  }
  counts: TextAnalysisCounts
}

export interface CardMark {
  start: number
  end: number
}

/** POST /texts/flow/gaps: one entry per submitted form, in the order sent. */
export interface TextGapMatchResult {
  gaps: {
    submitted: string
    /** The stored form (normalized, case-resolved); null when it has no word. */
    form: string | null
    /** What the save would store; empty when the save would refuse the form. */
    occurrences: [number, number][]
  }[]
}

export type TextDestinationKind = 'new_deck' | 'existing_deck' | 'none'

export interface TextFlowPayload {
  title: string
  body: string
  forms: string[]
  card_marks: CardMark[]
  destination: {
    kind: TextDestinationKind
    deck_id?: string | null
    is_active_for_learning?: boolean
  }
  save_text: boolean
  language?: string | null
}

/** Words left today (null when the counter cannot be read) and when it refreshes. */
export interface TextBudgetState {
  remaining: number | null
  /** Zone-less UTC, like every API datetime: parse it with a trailing Z. */
  refresh_at: string
}

export interface PlannedCardWord {
  word: string
  lemma: string
  start: number
}

export interface TextBudgetCheck {
  budget: TextBudgetState
  accepted: PlannedCardWord[]
  waiting: PlannedCardWord[]
  skipped: PlannedCardWord[]
}

export interface TextWaitingWord {
  id: string
  word: string
  lemma: string
  deck_id: string
  position: number
  wait_until: string
}

/** POST /texts/{id}/waiting/add: the one-tap add of waiting words (ADR-115 Amendment 2). */
export interface TextWaitingAddResult {
  /** The generation run to watch; null when nothing was accepted. */
  text_import_id: string | null
  cards_accepted: number
  /** Words the deck already held: no card, no charge. */
  skipped: string[]
  still_waiting: TextWaitingWord[]
  budget: TextBudgetState
}

/** A gap's accepted answer (ADR-116 Decision 2). */
export interface TextGapAlternative {
  id: string
  answer: string
  created_at: string
}

/** The dictionary's read-only note on a gap (handoff §15.3). */
export interface TextGapReference {
  base_form: string
  translation: string | null
}

export interface TextGap {
  id: string
  /** The stored form, lower-cased: the gap's identity (ADR-115 Decision 3). */
  form: string
  occurrences: [number, number][]
  /** The classifier's label in the stored English vocabulary; null when it was not confident. */
  label: string | null
  label_corrected: boolean
  /** With `label_corrected`: the owner's label, or null for «don't show the form». */
  corrected_label: string | null
  reference: TextGapReference | null
  alternatives: TextGapAlternative[]
}

/** GET /texts/{id}/gaps/{gap}/label-options. */
export interface GapLabelOptions {
  correctable: boolean
  /** Stored English labels, in the picker's order. */
  forms: string[]
  max_custom_label_chars: number
  current_label: string | null
}

/** One gap's label change inside an edit: exactly one field is set. */
export interface GapLabelEdit {
  label?: string
  hide?: boolean
  clear?: boolean
}

export interface GapAlternativesEdit {
  add: string[]
  remove: string[]
}

/** POST /texts/{id}/preview: what an edit would do to the gaps, nothing written. */
export interface TextPreviewGap {
  form: string
  status: 'kept' | 'new' | 'removed'
  occurrence_count: number
  occurrences: [number, number][]
}

export interface TextPreview {
  gaps: TextPreviewGap[]
}

/** PATCH /texts/{id}: every field optional; each one given replaces that aspect whole. */
export interface TextUpdatePayload {
  title?: string
  body?: string
  forms?: string[]
  deck_ids?: string[]
  drop_waiting_ids?: string[]
  label_corrections?: Record<string, GapLabelEdit>
  alternatives?: Record<string, GapAlternativesEdit>
  /** Spans of the edited body to make cards of (ADR-115 Amendment 3). */
  card_marks?: CardMark[]
  /** The receiving deck: left out when the text has exactly one deck after the edit. */
  /** The receiving deck, always the learner's choice with card marks (ADR-115 Amendment 4): an own deck, or the edit's new deck. */
  card_destination?: { kind: 'new_deck' } | { kind: 'existing_deck'; deck_id: string }
  /** A deck the edit makes and links, with or without card marks. Made only by the save. */
  new_deck?: { title: string; is_active_for_learning: boolean }
}

/** What an edit's card marks did, reported as the flow save reports them. */
export interface TextEditCardsResult {
  deck_id: string
  text_import_id: string | null
  cards_accepted: number
  skipped: PlannedCardWord[]
  waiting: PlannedCardWord[]
  budget_hit: boolean
  budget: TextBudgetState
}

export interface TextUpdateResult extends TextDetail {
  removed_forms: string[]
  /** null when the edit carried no card marks. */
  cards: TextEditCardsResult | null
}

/** POST /texts/{id}/carded: spans of the body whose card the deck already holds. */
export interface TextCarded {
  deck_id: string
  words: { start: number; end: number; word: string; lemma: string }[]
}

export interface TextDetail {
  id: string
  title: string
  body: string
  /** The body's words, by the rule step 2 and the gap matcher share. */
  word_count: number
  language: string
  level: number
  deck_ids: string[]
  /** The same decks with their titles, oldest link first: the text screen's chips. */
  decks: { id: string; title: string }[]
  gaps: TextGap[]
  waiting_words: TextWaitingWord[]
  /** The owner's practice status (ADR-116): the attempt in progress and completed runs. */
  practice?: TextPracticeSummary | null
  /** The text's live share link, or null while it is private (ADR-115 Decision 10). */
  share?: TextShare | null
  created_at: string
  updated_at: string
}

/** One row of Library → Mine → «Тексты» (GET /texts). */
export interface TextListItem {
  id: string
  title: string
  /** The body's opening, whitespace folded one-for-one so `preview_gaps` index it. */
  preview: string
  /** Gap occurrences wholly inside `preview`, in reading order. */
  preview_gaps: [number, number][]
  gap_count: number
  level: number
  /** Linked decks, oldest link first; empty is «Без колоды». */
  decks: { id: string; title: string }[]
  waiting_count: number
  /** The list's order: the later of the last practice and creation. */
  activity_at: string
  last_practiced_at: string | null
  created_at: string
  /**
   * A deck's list asked with word counts: how many of the text's distinct words have
   * a card in that deck. null when not asked for or not computable (ADR-115 Decision 2).
   */
  words_in_deck?: number | null
  practice?: TextPracticeSummary | null
}

export interface TextList {
  items: TextListItem[]
  total: number
}

export interface TextFlowSaveResult {
  text: TextDetail | null
  deck_id: string | null
  /** The generation run to poll (text import's status endpoint). */
  text_import_id: string | null
  cards_accepted: number
  skipped: PlannedCardWord[]
  waiting: PlannedCardWord[]
  not_created: PlannedCardWord[]
  budget_hit: boolean
  budget: TextBudgetState
}

// ── Texts: practice (ADR-116; handoff §10–§14) ───────────────────────────────

export type PracticeOutcome = 'first' | 'hinted' | 'revealed'

/** A run of the body's own text, or one occurrence of a gap (never its words). */
export interface PracticeSegment {
  text: string | null
  gap_id: string | null
}

export interface PracticeGap {
  id: string
  places: number
  /** The stored English label the gap shows; null when it shows none. */
  label: string | null
  state: 'empty' | 'answered' | 'revealed'
  /** Answered: what was accepted. Revealed: the form. Otherwise null. */
  answer: string | null
  /** Once done: the text's own words at each occurrence, in reading order. */
  written: string[]
  /** Once a first letter was shown, while the gap is open. */
  first_letter: string | null
  wrong_count: number
  outcome: PracticeOutcome | null
}

export interface PracticeBankChip {
  form: string
  used: boolean
}

export interface PracticeAttemptState {
  id: string
  started_at: string
  active_gap_id: string | null
  bank_opened: boolean
  answered: number
  remaining: number
}

export interface PracticeView {
  text_id: string
  title: string
  level: number
  max_level: number
  segments: PracticeSegment[]
  gaps: PracticeGap[]
  bank_state: 'open' | 'closed' | 'none'
  bank: PracticeBankChip[] | null
  attempt: PracticeAttemptState | null
  can_accept: boolean
}

export type PracticeVerdict = 'correct' | 'fits' | 'wrong' | 'revealed' | 'ignored' | 'done' | 'duplicate'

export interface PracticeVerdictResponse {
  verdict: PracticeVerdict
  gap: PracticeGap
  attempt: PracticeAttemptState
  bank: PracticeBankChip[] | null
}

export interface PracticeCompletion {
  attempt_id: string
  total: number
  first_time_count: number
  hinted_count: number
  revealed_count: number
  bank_opened: boolean
  level_before: number
  level_after: number
  max_level: number
}

export interface TextPracticeSummary {
  in_progress: { started_at: string; answered: number; total: number } | null
  completed_count: number
  last_completed_at: string | null
}

// ── Texts: sharing (ADR-115 Decision 10, Implementation note 4; handoff §17) ─

/** POST /texts/{id}/share, and the text read's `share`: one live link at a time. */
export interface TextShare {
  token: string
  created_at: string
}

/** GET /shared/texts/{token}: the title, the body with the gaps cut out, and the counts. */
export interface SharedText {
  title: string
  segments: PracticeSegment[]
  gaps: { id: string; places: number; label: string | null }[]
  gap_count: number
  word_count: number
  /** Set only for a signed-in reader: their own text, or the copy they saved. */
  viewer: { own_text_id: string | null; copy_text_id: string | null } | null
}

/** POST /shared/texts/{token}/check: one answer judged, nothing stored. */
export interface SharedCheckResult {
  verdict: 'correct' | 'fits' | 'wrong' | 'revealed' | 'ignored'
  gap_id: string
  first_letter: string | null
  answer: string | null
  written: string[]
}
